import React, { useEffect, useRef, useState } from 'react';
import { Bot, History, X, Minimize2, Maximize2, Send, Clock, RefreshCw, Mic, MicOff, Paperclip, Image as ImageIcon, FileText, Plus } from 'lucide-react';
import api from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface ETA { hours?: number; days?: number; note?: string }
interface Message { id: string; type: 'user' | 'ai'; content: string; timestamp: Date; eta?: ETA }
interface Chat { id: string; title: string; messages: Message[]; timestamp: Date; lastMessage?: string; }

interface GlobalAIDrawerProps { isOpen: boolean; onClose: () => void; }

const GlobalAIDrawer: React.FC<GlobalAIDrawerProps> = ({ isOpen, onClose }) => {
  const { getThemeColors } = useTheme();
  const colors = getThemeColors();
  const [compact, setCompact] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [attachments, setAttachments] = useState<{ id: string; name: string; type: string; size: number; dataUrl?: string }[]>([]);
  const [chats, setChats] = useState<Chat[]>(() => {
    const saved = localStorage.getItem('global-ai-chats');
    if (!saved) return [];
    const parsed = JSON.parse(saved);
    return parsed.map((c: any) => ({ ...c, timestamp: new Date(c.timestamp), messages: c.messages.map((m: any)=>({ ...m, timestamp: new Date(m.timestamp) })) }));
  });
  const [currentChatId, setCurrentChatId] = useState<string>(() => {
    return localStorage.getItem('global-ai-current') || '';
  });
  const currentChat = chats.find(c => c.id === currentChatId);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && !currentChatId) {
      const id = `chat-${Date.now()}-${Math.random().toString(36).slice(2,7)}`;
      const nc: Chat = { id, title: 'New Chat', messages: [], timestamp: new Date() };
      setChats([nc, ...chats]);
      setCurrentChatId(id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => { localStorage.setItem('global-ai-chats', JSON.stringify(chats)); }, [chats]);
  useEffect(() => { if (currentChatId) localStorage.setItem('global-ai-current', currentChatId); }, [currentChatId]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [currentChat?.messages?.length]);

  const addMessage = (type: 'user'|'ai', content: string) => {
    if (!currentChat) return;
    const msg: Message = { id: Date.now().toString(), type, content, timestamp: new Date() };
    setChats(prev => prev.map(c => c.id === currentChat.id ? { ...c, messages: [...c.messages, msg], lastMessage: content.slice(0,50), timestamp: new Date(), title: c.messages.length === 0 && type==='user' ? content.slice(0,30) : c.title } : c));
  };

  const streamAIText = async (fullText: string) => {
    let streamed = '';
    for (let i=0;i<fullText.length;i+=3){
      streamed = fullText.slice(0, i+3);
      setChats(prev => prev.map(c => {
        if (c.id !== currentChatId) return c;
        const msgs = [...c.messages];
        const last = msgs[msgs.length-1];
        if (!last || last.type !== 'ai') return c;
        msgs[msgs.length-1] = { ...last, content: streamed };
        return { ...c, messages: msgs };
      }));
      await new Promise(r => setTimeout(r, 10));
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || !currentChat) return;
    setInput(''); setIsProcessing(true);
    addMessage('user', text);
    const atts = await Promise.all(attachments.map(async (a) => {
      if (a.dataUrl) return a;
      return a;
    }));
    try {
      const res = await api.post('/api/ai/command', { command: text, originalCommand: text, context: { source: 'global-drawer' }, attachments: atts });
      const aiText = res.data.message || 'Done';
      addMessage('ai', '');
      await streamAIText(aiText);
      const eta = res.data?.eta || parseEta(aiText);
      if (eta) {
        setChats(prev => prev.map(c => {
          if (c.id !== currentChatId) return c;
          const msgs = [...c.messages];
          const last = msgs[msgs.length-1];
          if (last && last.type==='ai') {
            msgs[msgs.length-1] = { ...last, eta };
          }
          return { ...c, messages: msgs };
        }));
      }
    } catch (e) {
      addMessage('ai', 'Sorry, I encountered an error.');
    } finally {
      setIsProcessing(false);
    }
  };

  const parseEta = (text: string): ETA | undefined => {
    const etaRegex = /(eta|estimated\s*time(?:\s*to)?\s*(?:complete|finish)?)[^\d]*([\d\.]+)\s*(hours?|hrs?|h)\b(?:[^\d]*([\d\.]+)\s*(days?|d)\b)?/i;
    const m = text.match(etaRegex);
    if (m) {
      const hours = parseFloat(m[2]);
      const days = m[4] ? parseFloat(m[4]) : undefined;
      return { hours: isNaN(hours) ? undefined : hours, days: isNaN(Number(days)) ? undefined : days };
    }
    return undefined;
  };

  const handleVoice = () => {
    if (!(window as any).webkitSpeechRecognition) {
      alert('Voice input not supported in this browser');
      return;
    }
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }
    const recognition = new (window as any).webkitSpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';
    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setInput(transcript);
      setTimeout(send, 0);
    };
    recognitionRef.current = recognition;
    recognition.start();
  };

  const handlePickFiles = () => fileInputRef.current?.click();
  const handleFilesSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const mapped = await Promise.all(files.map(async (f) => ({
      id: `${f.name}-${f.size}-${Date.now()}`,
      name: f.name,
      type: f.type || 'application/octet-stream',
      size: f.size,
    })));
    setAttachments(prev => [...prev, ...mapped]);
    e.target.value = '';
  };
  const removeAttachment = (id: string) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  };

  if (!isOpen) return null;
  return (
    <div className={`fixed bottom-0 right-0 z-50 border border-slate-200/50 dark:border-slate-700/50 bg-white/90 dark:bg-slate-800/90 backdrop-blur-xl shadow-2xl flex flex-col
      rounded-tl-none sm:rounded-tl-2xl w-full h-[82vh]
      ${compact ? 'sm:w-[420px] sm:h-[360px]' : 'sm:w-[860px] sm:h-[80vh]'}`}> 
      {/* Header */}
      <div className="p-3 border-b border-slate-200/50 dark:border-slate-700/50 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="p-1.5 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg"><Bot className="w-4 h-4 text-white"/></div>
          <div className="text-sm font-semibold text-slate-800 dark:text-slate-200">Sage</div>
        </div>
        <div className="flex items-center space-x-1">
          <button onClick={()=>setShowHistory(!showHistory)} className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"><History className="w-4 h-4 text-slate-500"/></button>
          <button onClick={()=>setCompact(!compact)} className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">{compact ? <Maximize2 className="w-4 h-4 text-slate-500"/> : <Minimize2 className="w-4 h-4 text-slate-500"/>}</button>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"><X className="w-4 h-4 text-slate-500"/></button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* History */}
        {showHistory && (
          <div className="hidden sm:flex w-64 border-r border-slate-200/50 dark:border-slate-700/50 p-2 flex-col">
            <div className="flex items-center justify-between mb-2 px-1">
              <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">History</div>
              <button onClick={() => {
                const id = `chat-${Date.now()}-${Math.random().toString(36).slice(2,7)}`;
                const nc: Chat = { id, title: 'New Chat', messages: [], timestamp: new Date() };
                setChats(prev => [nc, ...prev]);
                setCurrentChatId(id);
              }} className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700"><Plus className="w-4 h-4 text-slate-500"/></button>
            </div>
            <div className="flex-1 overflow-y-auto stealth-scroll">
              {chats.map(c => (
                <div key={c.id} onClick={()=>setCurrentChatId(c.id)} className={`p-2 rounded-lg cursor-pointer text-sm mb-1 ${currentChatId===c.id ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
                  <div className="font-medium text-slate-700 dark:text-slate-200 truncate">{c.title}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{c.lastMessage}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 flex flex-col">
          <div className="flex-1 overflow-y-auto p-4 space-y-3 stealth-scroll">
            {currentChat?.messages.map(m => (
              <div key={m.id} className={`flex ${m.type==='user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`relative max-w-[80%] p-3 rounded-xl ${m.type==='user' ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white' : 'bg-white dark:bg-slate-800 border border-slate-200/50 dark:border-slate-700/50'}`}>
                  {m.type==='ai' && m.eta && (m.eta.hours || m.eta.days) && (
                    <div className="absolute -top-2 -right-2 text-[10px] px-2 py-0.5 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-md">
                      ETA: {m.eta.hours ? `${m.eta.hours}h` : ''}{m.eta.hours && m.eta.days ? ' · ' : ''}{m.eta.days ? `${m.eta.days}d` : ''}
                    </div>
                  )}
                  {m.type==='ai' ? (
                    <div className="prose prose-sm dark:prose-invert max-w-none">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                    </div>
                  ) : (
                    <div className="text-sm whitespace-pre-wrap">{m.content}</div>
                  )}
                  <div className={`text-[10px] mt-1 flex items-center space-x-1 ${m.type==='user' ? 'text-blue-100' : 'text-slate-500'}`}>
                    <Clock className="w-3 h-3"/>
                    <span>{m.timestamp.toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</span>
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef}/>
          </div>
          <div className="p-3 border-t border-slate-200/50 dark:border-slate-700/50">
            <div className="flex items-center space-x-2">
              <button onClick={handleVoice} className={`p-2 rounded-lg ${isListening ? 'bg-red-500 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200'}`} title="Voice input">
                {isListening ? <MicOff className="w-4 h-4"/> : <Mic className="w-4 h-4"/>}
              </button>
              <button onClick={handlePickFiles} className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200" title="Attach files">
                <Paperclip className="w-4 h-4"/>
              </button>
              <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf,.doc,.docx,.txt" className="hidden" onChange={handleFilesSelected}/>
              <textarea value={input} onChange={(e)=>setInput(e.target.value)} onKeyDown={(e)=>{ if(e.key==='Enter' && !e.shiftKey){ e.preventDefault(); send(); } }} placeholder="Ask Sage... (Shift+Enter for new line)" rows={1} style={{resize:'none'}}
                className="flex-1 p-2 rounded-lg border border-slate-200/50 dark:border-slate-700/50 bg-white/80 dark:bg-slate-800/80 text-slate-800 dark:text-slate-200 max-h-36 overflow-y-auto"
                onInput={(e)=>{
                  const el = e.currentTarget as HTMLTextAreaElement;
                  el.style.height = 'auto';
                  el.style.height = Math.min(el.scrollHeight, 144) + 'px';
                }}
              />
              <button onClick={send} disabled={(!input.trim() && attachments.length===0) || isProcessing} className={`px-3 py-2 rounded-lg text-white disabled:opacity-50 bg-gradient-to-r ${colors.primary}`}>
                {isProcessing ? <RefreshCw className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
              </button>
            </div>
            {attachments.length>0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {attachments.map(a => (
                  <div key={a.id} className="flex items-center space-x-1 px-2 py-1 rounded-md text-xs bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200">
                    {a.type.startsWith('image/') ? <ImageIcon className="w-3 h-3"/> : <FileText className="w-3 h-3"/>}
                    <span className="truncate max-w-[160px]">{a.name}</span>
                    <button onClick={()=>removeAttachment(a.id)} className="ml-1 opacity-70 hover:opacity-100">×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalAIDrawer;


